import React, { useState, useEffect, useRef } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { isAxiosError } from "axios";
import {
  Box,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Container,
  useMediaQuery,
  useTheme,
  CircularProgress,
  FormControl,
  FormLabel,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useApiClient from "../../api/Client";
import ENDPOINTS from "../../api/endpoints";
import AuthToggle from "../auth/AuthToggle";
import Icons from "@/assets";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import { setAccessToken } from "@/api/tokenManager";
import { ROLES } from "@/utils/constants";
import { getApiErrorMessage } from "@/utils/helper";
import { requestForToken } from "@/config/firebase";
import { useAuth } from "../../providers/AuthProvider";
import VerifyEmailModal from "./VerifyEmailModal";

const PRIMARY_GRADIENT = "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)";

// Styled components
const StyledContainer = styled(Container)(() => ({
  backgroundColor: "#F0EAFF",
  minHeight: "100vh",
}));

const SplashContainer = styled(Container)(({ theme }) => ({
  backgroundColor: "#7747FB",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
  backgroundSize: "cover",
  backgroundPosition: "center",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: theme.spacing(4),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "130%",
  letterSpacing: "0%",
  textAlign: "center",
  textTransform: "none",
  borderRadius: "24px",
  padding: theme.spacing(1.5, 4),
  margin: theme.spacing(1),
  backgroundImage: PRIMARY_GRADIENT,
  color: "#fff",
  "&:hover": {
    backgroundImage: "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)",
    boxShadow: "0 4px 12px rgba(157, 121, 244, 0.4)",
  },
  "&.Mui-disabled": {
    background: "#ccc",
    color: "#999",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  backgroundColor: "rgba(255, 255, 255, 1)",
  borderRadius: "24px",
  border: "1px solid rgba(217, 217, 217, 1)",
  "& .MuiOutlinedInput-root": {
    borderRadius: "24px",
    "& fieldset": {
      borderColor: "rgba(217, 217, 217, 1)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(217, 217, 217, 1)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(217, 217, 217, 1)",
    },
  },
  "& .MuiInputBase-input": {
    padding: theme.spacing(2),
  },
}));

const App: React.FC = () => {
  const [showSplash, setShowSplash] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  if (showSplash) {
    return (
      <SplashContainer>
        <Box
          component="img"
          src={Icons.LogoWhite}
          alt="logo"
          sx={{
            width: isMobile ? "80%" : "60%",
            maxWidth: "200px",
            height: "auto",
          }}
        />
      </SplashContainer>
    );
  }

  return (
    <>
      <RegisterPage />
    </>
  );
};

const RegisterPage: React.FC = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<"register" | "login">("login");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const { api } = useApiClient();
  const navigate = useNavigate();
  const [isCooldown, setIsCooldown] = useState(false);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleRegister = async () => {
    // Prevent multiple clicks
    if (isLoading) return;

    if (!username.trim()) {
      toast.warning("Username is required");
      return;
    }
    const usernameRegex = /[a-zA-Z0-9]/;
    if (!usernameRegex.test(username.trim())) {
      toast.warning("Username cannot contain only special characters");
      return;
    }
    if (!email.trim()) {
      toast.warning("Email ID is required");
      return;
    }
    const emailRegex = /^(?=.*[a-zA-Z].*@|.*@.*[a-zA-Z].*\.)[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!password.trim()) {
      toast.warning("Password is required");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{6,}$/;
    if (!passwordRegex.test(password)) {
      toast.error("Password must contain at least 1 uppercase letter, 1 number, and 1 special character");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (!executeRecaptcha) {
      toast.error("reCAPTCHA is not ready. Please try again later.");
      return;
    }

    setIsLoading(true);
    try {
      const recaptchaToken = await executeRecaptcha("register");

      const res = await api.post(ENDPOINTS.AUTH.REGISTER, {
        username: username.trim(),
        email: email.trim().toLowerCase(),
        password,
        role_name: ROLES.CONTRIBUTOR,
        recaptchaToken,
      });

      const confirmedEmail = res.data?.data?.user?.email || email.trim();
      
      toast.success(
        <span>
          A verification link has been sent to{" "}
          <strong>{confirmedEmail}</strong>. Please verify to continue.
        </span>
      );
      setActiveTab("login");
      clearForm();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Registration failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    if (isLoading || isCooldown) return;

    if (!username.trim() || !password.trim()) {
      toast.warning("Username and password are required");
      return;
    }

    if (!executeRecaptcha) {
      toast.error("reCAPTCHA is not ready. Please try again later.");
      return;
    }

    setIsLoading(true);
    try {
      const recaptchaToken = await executeRecaptcha("login");

      const res = await api.post(ENDPOINTS.AUTH.LOGIN, {
        username,
        password,
        role_name: ROLES.CONTRIBUTOR,
        recaptchaToken,
      });
      const token = res.data?.data?.token;
      if (token) {
        setAccessToken(token);
        toast.success("Login Successful!");
        navigate("/dashboard", { replace: true });

        // Request notification permission and save token
        try {
          const fcmToken = await requestForToken();
          if (fcmToken) {
            await api.post(ENDPOINTS.USER_DEVICES.MAIN, {
              fcm_token: fcmToken,
            });
            localStorage.setItem("fcm_token", fcmToken);
          }
        } catch (error) {
          console.error("Error setting up notifications:", error);
        }
      }
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        if (
          err?.response?.status === 401 &&
          err?.response?.data?.message?.includes("verify")
        ) {
          if (err?.response?.data?.data?.email) {
            setUnverifiedEmail(err.response.data.data.email);
          } else {
            toast.error(getApiErrorMessage(err, "Email not verified"));
          }
        } else {
          toast.error(getApiErrorMessage(err, "Login failed"));
        }
      } else {
        toast.error("Login failed");
      }
      // Debounce: prevent rapid re-clicks from showing duplicate toasts
      setIsCooldown(true);
      debounceTimeoutRef.current = setTimeout(() => {
        setIsCooldown(false);
        debounceTimeoutRef.current = null;
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    setIsResendLoading(true);
    try {
      const res = await api.post(ENDPOINTS.AUTH.RESEND_VERIFICATION, {
        email: unverifiedEmail,
      });
      setUnverifiedEmail(null);
      toast.success(res.data?.message || "Verification email resent!");
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Failed to resend verification"));
    } finally {
      setIsLoading(false);
    }
  };

  const clearForm = () => {
    setUsername("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
  };

  const renderPasswordAdornment = (
    isVisible: boolean,
    toggleVisibility: () => void,
    disabled: boolean
  ) => (
    <InputAdornment position="end">
      <IconButton
        onClick={toggleVisibility}
        edge="end"
        disabled={disabled}
      >
        <Box
          component={"img"}
          src={isVisible ? Icons.eyeoff : Icons.eyeon}
          sx={{
            height: { xs: "5.83vw", sm: 24 },
            width: { xs: "5.83vw", sm: 24 },
          }}
        />
      </IconButton>
    </InputAdornment>
  );

  return (
    <StyledContainer>
      <LogoContainer>
        <Box
          component="img"
          src={Icons.WheelChair}
          alt="Wheelchair"
          sx={{
            width: { xs: "64.81vw", sm: 160 },
            height: { xs: "64.81vw", sm: 160 },
            marginBottom: "4%",
            mt: "14%",
          }}
        />
        <img src={Icons.Logo} alt="logo" height={50} width={160} />
      </LogoContainer>

      <Box sx={{ width: "100%" }}>
        <AuthToggle
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isLoading={isLoading}
          clearForm={clearForm}
        />
        <FormControl fullWidth>
          {/* Inputs */}
          <FormLabel
            sx={{
              fontSize: "14px",
              color: "rgba(0, 0, 0, 1)",
              mb: "2%",
              ml: "2%",
            }}
          >
            User name
          </FormLabel>
          <StyledTextField
            fullWidth
            variant="outlined"
            value={username}
            placeholder="Enter username"
            onChange={(e) => {
              // Remove leading and trailing spaces, but allow spaces in between
              const value = e.target.value
                .replace(/^\s+/, "")
                .replace(/\s+$/, "");
              setUsername(value);
            }}
            onBlur={(e) => {
              // Trim whitespace on blur as well for consistency
              const value = e.target.value.trim();
              setUsername(value);
            }}
            disabled={isLoading}
          />
        </FormControl>

        {activeTab === "register" && (
          <FormControl fullWidth>
            <FormLabel
              sx={{
                fontSize: "14px",
                color: "rgba(0, 0, 0, 1)",
                mb: "2%",
                ml: "2%",
              }}
            >
              Email ID
            </FormLabel>
            <StyledTextField
              fullWidth
              variant="outlined"
              value={email}
              placeholder="Enter your email"
              onChange={(e) => setEmail(e.target.value)}
              onBlur={(e) => setEmail(e.target.value.trim())}
              disabled={isLoading}
            />
          </FormControl>
        )}
        <FormControl fullWidth>
          {/* Inputs */}
          <FormLabel
            sx={{
              fontSize: "14px",
              color: "rgba(0, 0, 0, 1)",
              mb: "2%",
              ml: "2%",
            }}
          >
            Password
          </FormLabel>
          <StyledTextField
            fullWidth
            type={showPassword ? "text" : "password"}
            variant="outlined"
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            InputProps={{
              endAdornment: renderPasswordAdornment(
                showPassword,
                () => !isLoading && setShowPassword(!showPassword),
                isLoading
              ),
            }}
          />
          
          {activeTab === "login" && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", m: "-2vw 2vw 2vw" }}>
              <Typography
                sx={{
                  color: "#9068f6",
                  fontSize: { xs: "3.2vw", sm: "14px" },
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={() => navigate("/forgot-password")}
              >
                Forgot password?
              </Typography>
            </Box>
          )}
        </FormControl>

        {/* Only show confirm password for registration */}
        {activeTab === "register" && (
          <FormControl fullWidth>
            {/* Inputs */}
            <FormLabel
              sx={{
                fontSize: "14px",
                color: "rgba(0, 0, 0, 1)",
                mb: "2%",
                ml: "2%",
              }}
            >
              Confirm Password
            </FormLabel>
            <StyledTextField
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              variant="outlined"
              value={confirmPassword}
              placeholder="Enter confirm password"
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              InputProps={{
                endAdornment: renderPasswordAdornment(
                  showConfirmPassword,
                  () => !isLoading && setShowConfirmPassword(!showConfirmPassword),
                  isLoading
                ),
              }}
            />
          </FormControl>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 0 }}>
          <StyledButton
            variant="contained"
            onClick={activeTab === "register" ? handleRegister : handleLogin}
            sx={{ width: "80%" }}
            disabled={isLoading || (activeTab === "login" && isCooldown)}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : activeTab === "register" ? (
              "Register"
            ) : (
              "Login"
            )}
          </StyledButton>
        </Box>

        <Box sx={{ mt: "4%", px: "3%" }}>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(0, 0, 0, 0.6)",
              fontSize: "12px",
              textAlign: "center",
              display: "block",
              mb: "2%",
            }}
          >
            By continuing, you agree to our{" "}
            <span
              onClick={() => setPrivacyPolicyOpen(true)}
              style={{ color: "#7747FB", textDecoration: "none", cursor: "pointer" }}
            >
              Privacy Policy
            </span>
            .
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(0, 0, 0, 0.6)",
              fontSize: "12px",
              textAlign: "center",
              display: "block",
            }}
          >
            This site is protected by reCAPTCHA and the Google{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#7747FB", textDecoration: "none" }}
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#7747FB", textDecoration: "none" }}
            >
              Terms of Service
            </a>{" "}
            apply.
          </Typography>
        </Box>
      </Box>

      <VerifyEmailModal
        open={!!unverifiedEmail}
        onClose={() => setUnverifiedEmail(null)}
        email={unverifiedEmail || ""}
        onResend={handleResendVerification}
        isLoading={isResendLoading}
      />

      <PrivacyPolicyModal
        open={privacyPolicyOpen}
        onClose={() => setPrivacyPolicyOpen(false)}
      />
    </StyledContainer>
  );
};

export default App;
